/**
 * Mount de la pantalla `/greenie-board.html` — Fase 5g.3 + 5g.5d.
 *
 * Flujo:
 *  1. `mountChrome({..., allowAnonymous:true})` prepara el chrome militar
 *     estándar. El tablero es público de solo lectura. De ahí sale
 *     `user`: si es admin/superadmin, el modal de pase se abre en modo
 *     editable (5g.5d).
 *  2. Inyecta el shell del tablero y carga los apontajes con `load()`,
 *     que es repintable: tras una corrección admin se vuelve a llamar
 *     para refrescar las filas.
 *  3. Al pinchar un cuadro de pase se abre el modal de detalle con la
 *     nota del LSO decodificada (5g.4) y, para admins, los controles de
 *     edición (5g.5d). Se llega aquí desde la ready room de
 *     `/portaaviones.html`.
 */

import type { GreenieBoardPilot, GreeniePass } from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { fetchGreenieBoard } from './api.js';
import {
  formatUpdated,
  renderBoardShell,
  renderErrorRow,
  renderFiltersSidebar,
  renderLoading,
  renderRows,
} from './markup.js';
import { openPassModal } from './pass-modal.js';

import './greenie-board.css';

export async function mountGreenieBoard(): Promise<void> {
  const { contentRoot, user } = await mountChrome({
    activeSection: 'greenie-board',
    allowAnonymous: true,
  });
  // Solo los Comandantes (admin+) pueden corregir apontajes a mano.
  const canEdit =
    user !== null && (user.role === 'admin' || user.role === 'superadmin');

  contentRoot.innerHTML = renderBoardShell();

  // Sidebar derecho: sustituir el `.chrome-actions` placeholder por el
  // bloque FILTROS. Convive con el chrome ya montado por mountChrome.
  const actionsHost = document.querySelector<HTMLElement>(
    '.chrome-sidebar--right .chrome-actions',
  );
  if (actionsHost) actionsHost.outerHTML = renderFiltersSidebar();

  // querySelector devuelve `| null`; tras comprobar, rebindeamos a consts
  // no-nullas para que el tipo sobreviva dentro de `load()`/`openFromCell`.
  const rowsRaw = contentRoot.querySelector<HTMLElement>('[data-zone="rows"]');
  const emptyRaw = contentRoot.querySelector<HTMLElement>(
    '[data-zone="empty"]',
  );
  const updatedEl = contentRoot.querySelector<HTMLElement>(
    '[data-zone="updated"]',
  );
  if (!rowsRaw || !emptyRaw) {
    throw new Error('[greenie-board] shell incompleto tras el render');
  }
  const rowsZone: HTMLElement = rowsRaw;
  const emptyZone: HTMLElement = emptyRaw;

  // Pilotos cargados del backend (fuente de verdad sin filtrar) y vista
  // filtrada que se pinta. Cuando cambian los filtros, recomputamos
  // `viewPilots` a partir de `pilots` sin volver al backend. El handler
  // del clic en un pase indexa contra `viewPilots` (las `data-pilot`/
  // `data-pass` del markup son posiciones en la vista renderizada).
  let pilots: GreenieBoardPilot[] = [];
  let viewPilots: GreenieBoardPilot[] = [];

  // Estado de los filtros del sidebar. `dateFrom`/`dateTo` son ISO
  // `YYYY-MM-DD` tal como los pinta `<input type="date">`; cadena vacía
  // = sin límite por ese lado.
  const filters: GreenieFilters = {
    campaign: '',
    mission: '',
    dateFrom: '',
    dateTo: '',
  };
  const campaignSel = document.querySelector<HTMLSelectElement>(
    '#gb-filter-campaign',
  );
  const missionSel = document.querySelector<HTMLSelectElement>(
    '#gb-filter-mission',
  );
  const dateFromInput = document.querySelector<HTMLInputElement>(
    '#gb-filter-date-from',
  );
  const dateToInput = document.querySelector<HTMLInputElement>(
    '#gb-filter-date-to',
  );
  const clearBtn = document.querySelector<HTMLButtonElement>(
    '#gb-filter-clear',
  );
  const filterInfo = document.querySelector<HTMLElement>(
    '[data-zone="filter-info"]',
  );

  /** Carga (o recarga) los apontajes desde el backend. */
  async function load(): Promise<void> {
    rowsZone.innerHTML = renderLoading();
    emptyZone.hidden = true;

    const result = await fetchGreenieBoard();
    if (!result.ok) {
      rowsZone.innerHTML = renderErrorRow(result.message);
      if (updatedEl) updatedEl.textContent = 'sin conexión';
      pilots = [];
      return;
    }

    pilots = result.data.pilots;
    if (updatedEl) {
      updatedEl.textContent = formatUpdated(result.data.generatedAt);
    }
    populateFilterSelects();
    renderView();
  }

  /**
   * Filtra `pilots` con `filters` y repinta el cuerpo del tablero.
   * El listener de pases sigue cableado a `rowsZone` (delegación), así
   * que pasa de una vista a otra sin recablear.
   */
  function renderView(): void {
    const filtered = applyFilters(pilots, filters);
    viewPilots = filtered;

    if (filtered.length === 0) {
      rowsZone.innerHTML = '';
      // Reutilizamos el mensaje "sin apontajes" cuando los filtros vacían
      // la vista — semánticamente equivale a "no hay nada que mostrar".
      emptyZone.hidden = false;
      updateFilterInfo(0, 0);
      return;
    }
    emptyZone.hidden = true;

    rowsZone.innerHTML = renderRows(filtered);

    // Igualamos el ancho de todas las tiras al historial más largo
    // (`--gb-cols`) ya filtrado: si el filtro deja un máximo de 3 pases,
    // la tira ocupa 3 columnas (no las 14 del histórico completo).
    const maxCols = Math.max(1, ...filtered.map((p) => p.passes.length));
    rowsZone.style.setProperty('--gb-cols', String(maxCols));
    rowsZone.scrollLeft = 0;

    const totalPasses = filtered.reduce((s, p) => s + p.passes.length, 0);
    updateFilterInfo(filtered.length, totalPasses);
  }

  function updateFilterInfo(pilotCount: number, passCount: number): void {
    if (!filterInfo) return;
    const noFilter =
      filters.campaign === '' &&
      filters.mission === '' &&
      filters.dateFrom === '' &&
      filters.dateTo === '';
    if (noFilter) {
      filterInfo.textContent = `${String(pilotCount)} pilotos · ${String(passCount)} pases`;
    } else {
      filterInfo.textContent = `Mostrando ${String(pilotCount)} pilotos · ${String(passCount)} pases`;
    }
  }

  /**
   * Rellena los selects de CAMPAÑA y MISIÓN con las opciones únicas
   * que aparecen en los datos cargados. Se llama tras cada `load()`
   * (los datos pudieron cambiar tras una edición admin). Si el usuario
   * tenía un filtro seleccionado y esa opción sigue existiendo, se
   * conserva; si desaparece, se resetea a "TODAS".
   */
  function populateFilterSelects(): void {
    if (!campaignSel || !missionSel) return;

    const passes = pilots.flatMap((p) => p.passes);
    const campaigns = uniqueSorted(
      passes.map((p) => p.campaign).filter((c): c is string => c !== null),
    );
    setSelectOptions(campaignSel, campaigns, filters.campaign);

    // Las misiones del select dependen de la campaña actualmente
    // seleccionada (si la hay), para no ofrecer misiones de otra
    // campaña que dejarían el tablero vacío al combinarlas.
    refreshMissionSelect();
  }

  function refreshMissionSelect(): void {
    if (!missionSel) return;
    const passes = pilots.flatMap((p) => p.passes);
    const filtered =
      filters.campaign === ''
        ? passes
        : passes.filter((p) => p.campaign === filters.campaign);
    const missions = uniqueSorted(filtered.map((p) => p.missionName));
    setSelectOptions(missionSel, missions, filters.mission);
  }

  // Wiring de los selects + botón LIMPIAR.
  campaignSel?.addEventListener('change', () => {
    filters.campaign = campaignSel.value;
    // Si la misión seleccionada ya no pertenece a la nueva campaña,
    // la reseteamos. Tras refrescar el select, comprobamos si sigue
    // siendo una opción válida.
    refreshMissionSelect();
    if (missionSel && !optionExists(missionSel, filters.mission)) {
      filters.mission = '';
      missionSel.value = '';
    }
    renderView();
  });
  missionSel?.addEventListener('change', () => {
    filters.mission = missionSel.value;
    renderView();
  });
  dateFromInput?.addEventListener('change', () => {
    filters.dateFrom = dateFromInput.value;
    renderView();
  });
  dateToInput?.addEventListener('change', () => {
    filters.dateTo = dateToInput.value;
    renderView();
  });
  clearBtn?.addEventListener('click', () => {
    filters.campaign = '';
    filters.mission = '';
    filters.dateFrom = '';
    filters.dateTo = '';
    if (campaignSel) campaignSel.value = '';
    refreshMissionSelect();
    if (missionSel) missionSel.value = '';
    if (dateFromInput) dateFromInput.value = '';
    if (dateToInput) dateToInput.value = '';
    renderView();
  });

  // Clic / teclado en un cuadro de pase → modal de detalle (5g.4) o, si
  // el usuario es admin, editable (5g.5d). Tras guardar, `load` refresca
  // el tablero. El listener se delega en `rowsZone` y sobrevive a los
  // repintados de `load`.
  const openFromCell = (cell: HTMLElement): void => {
    const pass =
      viewPilots[Number(cell.dataset.pilot)]?.passes[Number(cell.dataset.pass)];
    if (pass) openPassModal(pass, { canEdit, onSaved: load });
  };
  rowsZone.addEventListener('click', (ev) => {
    const cell = (ev.target as HTMLElement).closest<HTMLElement>('.gb-pass');
    if (cell) openFromCell(cell);
  });
  rowsZone.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const cell = (ev.target as HTMLElement).closest<HTMLElement>('.gb-pass');
    if (!cell) return;
    ev.preventDefault();
    openFromCell(cell);
  });

  await load();
}

// ─── Helpers de filtrado ─────────────────────────────────────────────────

interface GreenieFilters {
  campaign: string;
  mission: string;
  /** ISO `YYYY-MM-DD`, o '' si no hay límite inferior. */
  dateFrom: string;
  /** ISO `YYYY-MM-DD`, o '' si no hay límite superior. */
  dateTo: string;
}

/**
 * Aplica los filtros del sidebar a los pilotos cargados:
 *  - Filtra cada pase por `campaign === filters.campaign` (si hay
 *    filtro de campaña), `missionName === filters.mission` (si hay
 *    filtro de misión) y la parte fecha (`YYYY-MM-DD`) del `dateIso`
 *    dentro del rango `[dateFrom, dateTo]` (cualquier extremo vacío
 *    = sin límite por ese lado).
 *  - Los pilotos cuyos pases quedan en cero se omiten para no pintar
 *    filas vacías. Los totales del callsign (avgGradePoints/trapCount/
 *    totalPasses) se recalculan sobre la vista filtrada para que las
 *    cifras de la cabecera sean coherentes con lo que se ve.
 */
function applyFilters(
  pilots: GreenieBoardPilot[],
  filters: GreenieFilters,
): GreenieBoardPilot[] {
  const matchesPass = (pass: GreeniePass): boolean => {
    if (filters.campaign !== '' && pass.campaign !== filters.campaign) {
      return false;
    }
    if (filters.mission !== '' && pass.missionName !== filters.mission) {
      return false;
    }
    // El `dateIso` puede ser un timestamp completo; comparamos solo
    // la parte de fecha (`YYYY-MM-DD`) — lo que pinta `<input type="date">`.
    const passDate = pass.dateIso.slice(0, 10);
    if (filters.dateFrom !== '' && passDate < filters.dateFrom) return false;
    if (filters.dateTo !== '' && passDate > filters.dateTo) return false;
    return true;
  };

  const out: GreenieBoardPilot[] = [];
  for (const p of pilots) {
    const passes = p.passes.filter(matchesPass);
    if (passes.length === 0) continue;
    let trapCount = 0;
    let pointsSum = 0;
    let pointsCount = 0;
    for (const pass of passes) {
      if (pass.trapped) trapCount++;
      if (pass.averagePoints !== null) {
        pointsSum += pass.averagePoints;
        pointsCount++;
      }
    }
    out.push({
      callsign: p.callsign,
      totalPasses: passes.length,
      trapCount,
      avgGradePoints: pointsCount > 0 ? pointsSum / pointsCount : null,
      passes,
    });
  }
  return out;
}

/** Devuelve los valores únicos del array, ordenados alfabéticamente. */
function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

/**
 * Reescribe las `<option>` de un `<select>` conservando la primera
 * ("— TODAS —"). Tras la reescritura, intenta restaurar `selected` si
 * el valor previo aún existe.
 */
function setSelectOptions(
  sel: HTMLSelectElement,
  values: string[],
  selected: string,
): void {
  // Conservar la primera (placeholder "— TODAS —") y reconstruir el resto.
  const placeholder = sel.querySelector('option[value=""]');
  sel.innerHTML = '';
  if (placeholder) sel.appendChild(placeholder);
  else {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = '— TODAS —';
    sel.appendChild(opt);
  }
  for (const v of values) {
    const opt = document.createElement('option');
    opt.value = v;
    opt.textContent = v;
    sel.appendChild(opt);
  }
  sel.value = optionExists(sel, selected) ? selected : '';
}

function optionExists(sel: HTMLSelectElement, value: string): boolean {
  for (const opt of Array.from(sel.options)) {
    if (opt.value === value) return true;
  }
  return false;
}
